import { inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { GetLoadCarrierTemplatesResponse, LoadCarrierTemplate } from 'api/models';
import { LoadCarrierTemplatesService } from 'api/services';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';

/**
 * Shared cache for load-carrier templates (static reference data).
 *
 * Previously carrier-exchange, loadcarrier-swap and load-carrier-swap-consignee each
 * fetched + localStorage-cached this list independently. This service is the single
 * source: it fetches once, exposes the templates as a signal, and falls back to the
 * localStorage cache on error (preserving the original behaviour/key).
 */
@Injectable({ providedIn: 'root' })
export class LoadCarrierTemplatesRefService {
    private readonly loadCarrierTemplatesService = inject(
        LoadCarrierTemplatesService
    );
    private readonly sessionService = inject(SessionService);
    private readonly storageService = inject(StorageService);

    private readonly templates = signal<LoadCarrierTemplate[]>([]);
    /** All load-carrier templates (reference data). */
    readonly allCarrierTypes = this.templates.asReadonly();

    private inFlight: Promise<void> | null = null;

    /** Loads templates once; subsequent calls reuse the cached signal. */
    async load(): Promise<void> {
        if (this.templates().length > 0) {
            return;
        }
        if (this.inFlight) {
            return this.inFlight;
        }
        this.inFlight = this.fetch();
        try {
            await this.inFlight;
        } finally {
            this.inFlight = null;
        }
    }

    private async fetch(): Promise<void> {
        const orgKey = this.sessionService.getSession().organizations[0]._key;
        try {
            const response: GetLoadCarrierTemplatesResponse = await lastValueFrom(
                this.loadCarrierTemplatesService.getLoadCarrierTemplates({
                    organizationKey: orgKey,
                })
            );
            this.storageService.setItem('allCarrierTypes', response);
            this.templates.set(response);
        } catch {
            this.templates.set(
                this.storageService.getItemAsObject('allCarrierTypes') ?? []
            );
        }
    }
}
